/**
 * Fetches all `product_property` metaobjects via the Storefront API and exposes
 * them as window.constructorFiltersMapping for constructor-facets.js.
 *
 * Bypasses the 50-entry cap of `shop.metaobjects.<type>.values` in Liquid.
 *
 * Output shape:
 *   window.constructorFiltersMapping = {
 *     "40152": { label: "Burgundy", color: "#800020" },
 *     "249162": { label: "Hobby and Sport", image: "https://cdn.shopify.com/.../image.png" },
 *     ...
 *   };
 *
 * Dependencies (set in snippets/theme-strings.liquid):
 *   - window.shopDomain
 *   - window.themeSettings.custom_app_token  (Storefront access token)
 */
(function () {
  const STOREFRONT_API_VERSION = '2025-10';
  const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

  const token = window.themeSettings?.custom_app_token;
  const domain = window.shopDomain;
  if (!token || !domain) {
    window.constructorFiltersMapping = window.constructorFiltersMapping || {};
    window.constructorFiltersMappingReady = Promise.resolve(window.constructorFiltersMapping);
    return;
  }

  // Storefront API @inContext expects a LanguageCode enum (e.g. EN, HR, SK).
  // Strip any region suffix ("pt-BR" -> "PT") so we never send an invalid value.
  const LANG = (window.Shopify?.locale || 'EN').split('-')[0].toUpperCase();
  const COUNTRY = window.Shopify?.country || 'SK';

  const endpoint = `https://${domain}/api/${STOREFRONT_API_VERSION}/graphql.json`;
  const CACHE_KEY = `constructorFiltersMapping:v1:${LANG}:${COUNTRY}`;

  const QUERY = `
    query GetProductProperties($cursor: String, $lang: LanguageCode!, $country: CountryCode!) @inContext(language: $lang, country: $country) {
      metaobjects(type: "product_property", first: 250, after: $cursor) {
        nodes {
          handle
          valueId: field(key: "value_id") { value }
          label: field(key: "label") { value }
          visualColor: field(key: "visual_color") { value }
          visualImage: field(key: "visual_image") {
            reference {
              __typename
              ... on MediaImage { image { url(transform: { maxWidth: 200 }) } }
              ... on GenericFile { url }
            }
          }
        }
        pageInfo { hasNextPage endCursor }
      }
    }
  `;

  function imageUrlFromReference(ref) {
    if (!ref) return '';
    if (ref.__typename === 'MediaImage') return ref.image?.url || '';
    if (ref.__typename === 'GenericFile') return ref.url || '';
    return '';
  }

  async function fetchAll() {
    try {
      const cached = sessionStorage.getItem(CACHE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed?.expiresAt > Date.now() && parsed.map && Object.keys(parsed.map).length) {
          return parsed.map;
        }
      }
    } catch (_) {
      // sessionStorage unavailable / quota / stale schema — fall through to network.
    }

    const map = {};
    let cursor = null;
    let hadError = false;

    try {
      do {
        const res = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': token,
          },
          body: JSON.stringify({ query: QUERY, variables: { cursor, lang: LANG, country: COUNTRY } }),
        });

        if (!res.ok) {
          hadError = true;
          console.error(
            '[constructor] product_property HTTP error',
            res.status,
            res.statusText,
            await res.text().catch(() => ''),
          );
          window.reportFrontendError?.({
            source: 'constructor',
            surface: 'ConstructorProductProperties',
            message: `product_property HTTP ${res.status} ${res.statusText}`,
            error_type: 'product_properties',
          });
          break;
        }

        const json = await res.json();
        if (json?.errors?.length) {
          hadError = true;
          console.error('[constructor] product_property GraphQL errors', json.errors);
          window.reportFrontendError?.({
            source: 'constructor',
            surface: 'ConstructorProductProperties',
            message: json.errors[0]?.message || 'product_property GraphQL errors',
            error_type: 'product_properties',
          });
          break;
        }
        const conn = json?.data?.metaobjects;
        if (!conn) {
          hadError = true;
          console.error('[constructor] product_property: no metaobjects data in response', json);
          break;
        }

        (conn.nodes || []).forEach((node) => {
          const code = node.valueId?.value || node.handle;
          if (!code) return;
          const entry = { label: node.label?.value || '' };
          const image = imageUrlFromReference(node.visualImage?.reference);
          const color = node.visualColor?.value;
          if (image) entry.image = image;
          else if (color) entry.color = color;
          map[code] = entry;
        });

        cursor = conn.pageInfo?.hasNextPage ? conn.pageInfo.endCursor : null;
      } while (cursor);
    } catch (err) {
      hadError = true;
      console.error('[constructor] product_property fetch failed', err);
      window.reportFrontendError?.({
        source: 'constructor',
        surface: 'ConstructorProductProperties',
        message: err?.message || String(err),
        stack: err?.stack || '',
        error_type: 'product_properties',
      });
    }

    // Only cache if we got something back; never poison the cache with an empty/failed result.
    if (!hadError && Object.keys(map).length > 0) {
      try {
        sessionStorage.setItem(CACHE_KEY, JSON.stringify({ map, expiresAt: Date.now() + CACHE_TTL_MS }));
      } catch (_) {
        // ignore quota errors
      }
    }

    return map;
  }

  window.constructorFiltersMappingReady = fetchAll().then((map) => {
    window.constructorFiltersMapping = { ...(window.constructorFiltersMapping || {}), ...map };
    const count = Object.keys(window.constructorFiltersMapping).length;
    if (count === 0) {
      console.warn(
        '[constructor] product_property: no entries loaded — check Storefront API token scope and metaobject definition visibility.',
      );
    }
    window.dispatchEvent(new CustomEvent('constructor:filters-mapping-ready'));
    return window.constructorFiltersMapping;
  });
})();
